import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchLikes, toggleLike } from '../services/newsService';

interface Props {
  postId: string;
}

const LikeButton: React.FC<Props> = ({ postId }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchLikes(postId, user?.id).then(({ count: c, liked: l }) => {
      if (cancelled) return;
      setCount(c);
      setLiked(l);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [postId, user?.id]);

  const handleToggle = async () => {
    if (!user || toggling) return;
    setToggling(true);

    // Optimistic update
    const nextLiked = !liked;
    const nextCount = count + (nextLiked ? 1 : -1);
    setLiked(nextLiked);
    setCount(nextCount);

    const { count: confirmedCount, liked: confirmedLiked } = await toggleLike(postId, user.id, !nextLiked);
    setCount(confirmedCount);
    setLiked(confirmedLiked);
    setToggling(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 text-gray-700 text-sm select-none">
        <HeartIcon filled={false} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={!user || toggling}
      title={user ? (liked ? 'Fjern like' : 'Lik dette innlegget') : 'Logg inn for å like'}
      className={[
        'flex items-center gap-1.5 text-sm transition-all duration-150 select-none',
        'disabled:cursor-default',
        user
          ? liked
            ? 'text-red-500 hover:text-red-400'
            : 'text-gray-500 hover:text-red-400'
          : 'text-gray-700 cursor-default',
      ].join(' ')}
    >
      <HeartIcon filled={liked} />
      {count > 1 && <span className="tabular-nums leading-none">{count}</span>}
    </button>
  );
};

const HeartIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

export default LikeButton;
